import { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Platform } from 'react-native';
import { Stack, useLocalSearchParams, usePathname, useRouter, Redirect } from 'expo-router';
import { ErrorBoundary } from '../../../../components/layout';
import { CompetitionProvider } from '../../../../src/competitions/contexts/CompetitionContext';
import type { CompetitionContextValue } from '../../../../src/competitions/contexts/competitionContext.constants';
import {
  competitionBasePath,
  competitionTabRoute,
  getActiveCompetitionTab,
  type CompetitionTab,
} from '../../../../src/competitions/helpers/competitionRoutes.helper';
import { useCompetitionSummary, competitionDisplayTitle } from '../../../../src/competitions/hooks/useCompetitionSummary';
import { useCompetitionIntro } from '../../../../src/competitions/hooks/useCompetitionIntro';
import { getCompetitionIntroContent } from '../../../../src/competitions/constants/competitionIntroContent';
import { CompetitionWelcomeScreen } from '../../../../components/competitions/CompetitionWelcomeScreen/CompetitionWelcomeScreen';
import { useSupabaseAuth } from '../../../../src/shared/contexts/SupabaseAuthContext';
import { useMenuContext } from '../../../../src/layout/contexts/MenuContext';
import { CompetitionScreenHeader } from '../../../../components/competitions/CompetitionScreenHeader/CompetitionScreenHeader';
import { WorldCupTabBar } from '../../../../components/worldcup/WorldCupTabBar/WorldCupTabBar';
import { WORLD_CUP_TABS } from '../../../../components/worldcup/WorldCupTabBar/WorldCupTabBar.constants';
import {
  competitionTabsFor,
  redirectTabWhenUnavailable,
} from '../../../../src/competitions/helpers/competitionTabs.helper';
import { CompetitionStandingsModal } from '../../../../components/competitions/CompetitionStandingsModal/CompetitionStandingsModal';
import { bracketStages } from '../../../../src/competitions/helpers/tournamentBracket.helper';
import { WORLD_CUP_COMPETITION_ID } from '../../../../src/worldcup/worldcup.constants';
import { useTheme } from '../../../../src/theme/useTheme';
import { getWorldcupStyles } from '../../../../appStyles/_worldcup.styles';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

/** Matches the World Cup shell's stats-modal animation, so the two trees feel identical. */
const STATS_MODAL_ANIMATION_MS = 240;

/**
 * Competition shell (specs/competitions-hub-client Slice 3) — the generic twin of
 * app/app/worldcup/_layout.tsx: provider + header + tab bar + Stack.
 */
export default function CompetitionLayout() {
  const router = useRouter();
  const pathname = usePathname();
  const params = useLocalSearchParams<{ competitionId: string; wcTab?: string }>();
  const competitionId = params.competitionId ?? '';
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(
    () => getWorldcupStyles(theme, { safeAreaTop: insets.top }),
    [theme, insets.top],
  );

  const contextValue = useMemo<CompetitionContextValue>(
    () => ({
      competitionId,
      basePath: competitionBasePath(competitionId),
      surface: 'generic',
    }),
    [competitionId],
  );

  const { competition } = useCompetitionSummary(competitionId);
  const activeTab = getActiveCompetitionTab(pathname, 'generic', params);

  // The Leaderboard tab is earned, like the header's Table button (AC-11.8): a global board of
  // four names ranks nobody, while the squad board works from the first member. `undefined`
  // (a server predating `participantCount`) offers it, so the tab never vanishes on deploy skew.
  const offeredTabs = useMemo(
    () => competitionTabsFor(competition?.participantCount),
    [competition?.participantCount],
  );
  const tabDefs = useMemo(
    () => WORLD_CUP_TABS.filter((t) => offeredTabs.includes(t.key as CompetitionTab)),
    [offeredTabs],
  );

  // The tab bar is the same three every competition has — Picks, Leaderboard, Squads — exactly
  // as the World Cup's is (specs/champions-league Stories 5 & 6, revised). The table and the
  // bracket were briefly tabs four and five; they are reference material, so they moved to the
  // header's Table button and its modal, which is where the WC has always kept them.
  //
  // The panes are still EARNED, not assumed (AC-5.1, AC-6.1): the button appears only when this
  // competition publishes a table or its stage order actually contains knockout rounds. A season
  // pool therefore keeps its header unchanged.
  const [isStandingsOpen, setStandingsOpen] = useState(false);
  const hasStandingsModal = useMemo(() => {
    const rules = competition?.rules;
    if (!rules) return false;
    const hasBracket = bracketStages(
      rules.stageOrder ?? [], rules.multiRoundStages ?? {},
    ).length > 0;
    return rules.hasTournamentStandings === true || hasBracket;
  }, [competition?.rules]);

  // Story 10: the match hub and the pick-stats screen are full-screen surfaces, not tabs.
  // On native they present as modals, which cover the shell on their own. On web there is
  // no native modal, so the header and tab bar are hidden by path while either shows, and
  // restored on a timer once the dismiss animation has finished — otherwise they flash back
  // in over a route that is still sliding off.
  const isModalChromePath = Platform.OS === 'web' && (
    pathname.includes('/match/') || pathname.includes('/match-stats/')
  );
  const [shouldHideShellChrome, setShouldHideShellChrome] = useState(isModalChromePath);

  useEffect(() => {
    if (isModalChromePath) {
      setShouldHideShellChrome(true);
      return;
    }
    const restoreChromeTimer = setTimeout(() => {
      setShouldHideShellChrome(false);
    }, STATS_MODAL_ANIMATION_MS + 50);
    return () => clearTimeout(restoreChromeTimer);
  }, [isModalChromePath]);

  // Standing on a tab that is no longer offered — a deep link, or a board that has not yet
  // reached the threshold — leaves the viewer on a route no tab owns and a bar highlighting
  // nothing. `replace`, not `push`: the unavailable tab should not sit in the back stack.
  useEffect(() => {
    const redirect = redirectTabWhenUnavailable(activeTab, offeredTabs);
    if (redirect) {
      router.replace(competitionTabRoute(competitionBasePath(competitionId), redirect) as never);
    }
  }, [activeTab, offeredTabs, router, competitionId]);

  const openStandings = useCallback(() => setStandingsOpen(true), []);
  const closeStandings = useCallback(() => setStandingsOpen(false), []);

  // Season/tournament rules intro. Unlike the WC, an established player is NOT
  // backfilled — they still need this competition's rules explained once.
  const { isLoaded, isSignedIn, userId } = useSupabaseAuth();
  const introContent = useMemo(() => getCompetitionIntroContent(competition), [competition]);
  const { introUi, handleIntroFinished, showIntro } = useCompetitionIntro({
    competitionId,
    isLoaded,
    isSignedIn,
    userId: userId ?? null,
    enabled: introContent != null,
    contentId: introContent?.contentId ?? null,
  });

  const handleTabChange = useCallback(
    (tab: CompetitionTab) => {
      if (tab === activeTab) return;
      router.replace(competitionTabRoute(contextValue.basePath, tab) as never);
    },
    [activeTab, contextValue.basePath, router],
  );

  const handleBack = useCallback(() => {
    router.replace('/app/competitions');
  }, [router]);

  // Same hamburger the WC shell and PageHeader use: it opens the global menu
  // (Settings / Profile / Sign-out), it is not a shortcut to a screen.
  const { handleMenuPress } = useMenuContext();

  // The World Cup owns its own tree; a stray /app/competition/world-cup-2026 link
  // must not render the generic shell against WC data.
  if (competitionId === WORLD_CUP_COMPETITION_ID) {
    return <Redirect href="/app/worldcup" />;
  }

  return (
    <ErrorBoundary>
      <CompetitionProvider value={contextValue}>
        <View style={shouldHideShellChrome ? styles.containerMatchStatsModal : styles.container}>
          <View style={shouldHideShellChrome ? styles.shellHeaderHidden : styles.shellHeader}>
            <CompetitionScreenHeader
              title={competitionDisplayTitle(competition) || 'Competition'}
              subtitle={competition?.brand?.year ?? null}
              logoUrl={competition?.logoUrl ?? null}
              flagCode={competition?.brand?.flagCode ?? null}
              onBackPress={handleBack}
              onRulesPress={introContent != null ? showIntro : undefined}
              onStandingsPress={hasStandingsModal ? openStandings : undefined}
              onMenuPress={handleMenuPress}
            />
          </View>
          <View style={shouldHideShellChrome ? styles.tabBarHidden : undefined}>
            <WorldCupTabBar
              activeTab={activeTab}
              onTabChange={handleTabChange}
              tabs={tabDefs}
            />
          </View>
          <View style={styles.tabContent}>
            <Stack
              screenOptions={({ route }) => ({
                headerShown: false,
                contentStyle: styles.stackContentStyle,
                ...(route.name === 'member-profile'
                  ? {
                      animation: Platform.OS === 'web' ? 'fade' : 'slide_from_right',
                      animationDuration: Platform.OS === 'web' ? 150 : 240,
                      gestureEnabled: Platform.OS !== 'web',
                    }
                  : route.name === 'match-stats'
                  ? {
                      ...(Platform.OS !== 'web' ? { presentation: 'modal' as const } : {}),
                      animation: Platform.OS === 'web' ? 'slide_from_right' : 'slide_from_bottom',
                      animationDuration: STATS_MODAL_ANIMATION_MS,
                      gestureEnabled: true,
                    }
                  : route.name === 'match'
                  ? {
                      ...(Platform.OS !== 'web' ? { presentation: 'modal' as const } : {}),
                      animation: Platform.OS === 'web' ? 'fade' : 'slide_from_bottom',
                      gestureEnabled: true,
                    }
                  : {
                      animation: 'fade',
                      animationDuration: 150,
                    }),
              })}
            />
          </View>
          <CompetitionStandingsModal
            isVisible={isStandingsOpen}
            onDismiss={closeStandings}
            rules={competition?.rules}
          />
          {introContent != null && introUi === 'show' ? (
            <CompetitionWelcomeScreen
              content={introContent}
              title={competitionDisplayTitle(competition) || undefined}
              onEnter={() => { void handleIntroFinished(); }}
            />
          ) : null}
        </View>
      </CompetitionProvider>
    </ErrorBoundary>
  );
}
